import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import LocalizedText from '@LocalizedText';
import { performAction } from '@app/actions/customizeActions';
import { inject } from '@app/providers/QAU.ContextProvider';
import { isMobileMode } from '@app/utils/utils';

import Button from '@components/buttons/Button';
import './ScaleTool.scss';
import useIntlMessage from '@app/hooks/use-intl-message';

const ScaleTool = ({
  isMobileView,
  aspectRatioLocked,
  validateImage,
  performAction,
  flow: {
    editor: {
      context: { selector },
      selectedFields: [field],
    },
  },
}) => {
  const [
    scaleSizeUpAriaLabel,
    scaleSizeDownAriaLabel,
    scaleHeightUpAriaLabel,
    scaleHeightDownAriaLabel,
    scaleWidthUpAriaLabel,
    scaleWidthDownAriaLabel,
  ] = useIntlMessage(
    'qau-art-scale-size-up-aria-label',
    'qau-art-scale-size-down-aria-label',
    'qau-art-scale-height-up-aria-label',
    'qau-art-scale-height-down-aria-label',
    'qau-art-scale-width-up-aria-label',
    'qau-art-scale-width-down-aria-label',
  );
  const scaleArt = ({ parentNode }, direction) => {
    const scaleX = parentNode.classList?.contains('scale-by-width');
    const scaleY = parentNode.classList?.contains('scale-by-height');

    const scaleFactor = { down: 0.975, up: 1.025 };

    performAction('fieldScale', {
      sx: aspectRatioLocked || scaleX ? scaleFactor[direction] : 1,
      sy: aspectRatioLocked || scaleY ? scaleFactor[direction] : 1,
      origin: selector.getCenter(field),
    });

    validateImage();
  };

  const scale = ({ target }) => {
    const isScaleDown = target.classList.contains('down');
    const isScaleUp = target.classList.contains('up');
    let direction = null;

    direction = isScaleDown ? 'down' : direction;
    direction = isScaleUp ? 'up' : direction;

    scaleArt(target, direction);
  };

  const scaleToolCollapsedLayout = (
    <div className="scale-tool-collapsed">
      <Button className="scale-btn down" onClick={scale} aria-label={scaleSizeDownAriaLabel} />
      <Button className="scale-btn up" onClick={scale} aria-label={scaleSizeUpAriaLabel} />
    </div>
  );

  const scaleToolExpandedLayout = (
    <div className="scale-tool-expanded">
      <div className="scale-by-height">
        <LocalizedText id="qau-height-text" tagName="span" />
        <Button className="scale-btn down" onClick={scale} aria-label={scaleHeightDownAriaLabel} />
        <Button className="scale-btn up" onClick={scale} aria-label={scaleHeightUpAriaLabel} />
      </div>
      <div className="aspect-ration-unlocked-icon" />
      <div className="scale-by-width">
        <LocalizedText id="qau-width-text" tagName="span" />
        <Button className="scale-btn down" onClick={scale} aria-label={scaleWidthDownAriaLabel} />
        <Button className="scale-btn up" onClick={scale} aria-label={scaleWidthUpAriaLabel} />
      </div>
    </div>
  );

  return (
    <div id="qau-scale-tool">
      <div className={classnames('scale-tool-controls', { expanded: !aspectRatioLocked })}>
        <div className={classnames('scale-tool-title', { hidden: !aspectRatioLocked && isMobileView })}>
          <LocalizedText id="qau-scale-art-tool-text" tagName="div" />
        </div>
        {aspectRatioLocked ? scaleToolCollapsedLayout : scaleToolExpandedLayout}
      </div>
    </div>
  );
};

const mapStateToProps = ({
  customize: {
    customizeData: {
      scaleParams: { aspectRatioLocked, validateImage },
    },
  },
  matrix: { ctx },
}) => ({ aspectRatioLocked, validateImage, isMobileView: isMobileMode(ctx) });

const mapDispatchToProps = { performAction };

ScaleTool.propTypes = {
  aspectRatioLocked: PropTypes.bool.isRequired,
  performAction: PropTypes.func.isRequired,
  validateImage: PropTypes.func.isRequired,
  flow: PropTypes.object.isRequired,
  isMobileView: PropTypes.bool.isRequired,
};

export default inject('flow')(connect(mapStateToProps, mapDispatchToProps)(ScaleTool));
